import { useEffect, useRef, useState } from "react";
import { ANGKATAN_PERTAMA, ColourCodes } from "iasma-datatypes";
import { useNavigate } from "react-router-dom";
import { queryConfirmationSummary } from "@/services/EventService";
import { useAuth } from "@/contexts/AuthContext";
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import axios from "axios";
import { Pie, PieChart, ResponsiveContainer } from "recharts";
import { useNotify } from "@/contexts/NotifyContext";
import { NavBack } from "../layout/Menu/NavBack";
import { ChartConfirmationNames } from "./ChartConfirmationNames";

type dataType = {
	year: number;
	unknown: number;
	yes: number;
	maybe: number;
	no: number;
}

type chartDataType = {
	name: string;
	value: number;
	fill: string;
}


export default function ChartConfirmation({ eventId }: { eventId: number }) {

	const navigate = useNavigate();
	const { session, setToken } = useAuth();
	const { setNotify } = useNotify();
	const [data, setData] = useState<dataType[]>([]);
	const [chartData, setChartData] = useState<chartDataType[]>([]);
	const [totalYes, setTotalYes] = useState(0);
	const [totalMaybe, setTotalMaybe] = useState(0);
	const [totalNo, setTotalNo] = useState(0);
	const [showNames, setShowNames] = useState<number>(0);
	const rowref = useRef<null | HTMLTableCellElement>(null);

	useEffect(() => {
		setNotify(null);
		setToken(axios);
		queryConfirmationSummary(eventId).then(
			(recs) => {
				// console.log(recs);
				let year = 0;
				let totalYes = 0;
				let totalMaybe = 0;
				let totalNo = 0;
				let result: dataType[] = [];
				let data: dataType = { year: 0, unknown: 0, yes: 0, maybe: 0, no: 0 };
				let cd: chartDataType[] = [];
				let rc = Math.min(recs.length, 80);

				for (let i = 0; i < rc; i++) {
					const rec = recs[i];
					if (rec.Graduation !== year) {
						year = rec.Graduation;
						data = { year: year, unknown: 0, yes: 0, maybe: 0, no: 0 }
						result.push(data);
					}
					switch (rec.Confirmation) {
						case 0: data.unknown = rec.CNT; break;
						case 1:
							data.yes = rec.CNT;
							totalYes += rec.CNT;
							cd.push({
								name: year.toString(),
								value: rec.CNT,
								fill: ColourCodes[year - 1958]
							})
							break;
						case 2: data.maybe = rec.CNT; totalMaybe += rec.CNT; break;
						case 3: data.no = rec.CNT; totalNo += rec.CNT; break;
					}
				}
				setData(result);
				setChartData(cd);
				setTotalYes(totalYes);
				setTotalMaybe(totalMaybe);
				setTotalNo(totalNo);
			}, (error) => {
				console.log(error);
				setNotify({ title: "Confirmation List Fail", error, severity: 'error' });
			}
		)

	}, []);

	useEffect(() => {
		rowref.current?.scrollIntoView({ behavior: 'smooth' })
	});

	function onShowNamesClose() {
		setShowNames(0)
	}

	function onShowNames(year: number) {
		// navigate(`/sla/chart-confirmation-names/${year}`);
		setShowNames(year)
	}

	function newRow(i: number, onRowClick: (year: number) => void, rec: dataType) {
		if (session && session.User?.Alumni && session.User.Alumni.Graduation === (rec.year + 2)) {
			return <TableRow key={i} onClick={() => onRowClick(rec.year)}>
				<TableCell align="left">{rec.year}</TableCell>
				<TableCell align="right">{rec.yes}</TableCell>
				<TableCell align="right">{rec.maybe}</TableCell>
				<TableCell align="right" ref={rowref}>{rec.no}</TableCell>
			</TableRow>;
		} else {
			return <TableRow key={i} onClick={() => onRowClick(rec.year)}>
				<TableCell align="left">{rec.year}</TableCell>
				<TableCell align="right">{rec.yes}</TableCell>
				<TableCell align="right">{rec.maybe}</TableCell>
				<TableCell align="right">{rec.no}</TableCell>
			</TableRow>;
		}
	}

	const renderLabelInside = ({ cx, cy, midAngle, innerRadius, outerRadius, name, value, }: any) => {
		if (!value) return null;

		const RADIAN = Math.PI / 180;
		const radius = innerRadius + (outerRadius - innerRadius) / 1.5;

		const x = cx + radius * Math.cos(-midAngle * RADIAN);
		const y = cy + radius * Math.sin(-midAngle * RADIAN);

		return (
			<text
				x={x}
				y={y}
				fill="#fff"
				textAnchor="middle"
				dominantBaseline="central"
				fontSize={10}
				fontWeight="bold"
			>
				{name}
			</text>
		);
	};

	const renderLabelSmall = ({ cx, cy, midAngle, innerRadius, outerRadius, name, value, fill }: any) => {
		if (!value) return null;

		const RADIAN = Math.PI / 180;
		const radius = innerRadius + outerRadius;

		const x = cx + radius * Math.cos(-midAngle * RADIAN);
		const y = cy + radius * Math.sin(-midAngle * RADIAN);

		return (
			<text
				x={x}
				y={y}
				fill={fill}
				textAnchor="middle"
				dominantBaseline="central"
				fontSize={10}
			>
				{name}
			</text>
		);
	};

	if (showNames >= ANGKATAN_PERTAMA) {
		return <ChartConfirmationNames eventId={eventId} year={showNames} callback={onShowNamesClose} />
	} else {

		return (<div className="flex flex-col gap-2 items-center justify-center">
			<NavBack />
			{chartData.length > 0 && <div className="relative w-full">
				<ResponsiveContainer width="100%" height={350}>
					<PieChart>
						<Pie
							data={chartData}
							dataKey="value"
							nameKey="name"
							cx="50%"
							cy="50%"
							outerRadius={120}
							innerRadius={60}
							paddingAngle={0}
							label={renderLabelSmall}
							labelLine={true}
						/>
					</PieChart>
				</ResponsiveContainer>
				<div className="pointer-events-none absolute inset-0 flex items-center justify-center">
					<div className="text-center">
						<div className="text-2xl font-bold text-slate-800">{totalYes}</div>
						<div className="text-[10px] uppercase tracking-wide text-slate-500">orang</div>
					</div>
				</div>
			</div>}
			<div className="max-w-90">
				<div className="max-h-90 overflow-y-auto border" style={{ overflowX: "hidden" }}>
					<table className="w-full table-fixed border-collapse text-sm">
						<thead style={{ position: "sticky", top: 0, zIndex: 10, backgroundColor: "#f8fafc" }}>
							<tr>
								<th className="p-2 text-left text-sm">Angkatan</th>
								<th className="p-2 text-right text-sm">Hadir</th>
								<th className="p-2 text-right text-sm">Mungkin</th>
								<th className="p-2 text-right text-sm">Tidak</th>
							</tr>
						</thead>
						<tbody>
							{data.map((rec, i) => (newRow(i, onShowNames, rec)))}
						</tbody>
						<tfoot style={{ position: "sticky", bottom: 0, zIndex: 10, backgroundColor: "#f8fafc" }}>
							<tr>
								<td className="p-2 text-left text-sm"></td>
								<td className="p-2 text-right text-sm"><b>{totalYes}</b></td>
								<td className="p-2 text-right text-sm"><b>{totalMaybe}</b></td>
								<td className="p-2 text-right text-sm"><b>{totalNo}</b></td>
							</tr>
						</tfoot>
					</table>
				</div>
				<div className="p-2 text-[0.8em] italic text-center">klik dibaris angkatan untuak maliek namo konco nan katibo</div>
				<div>
					<button type="button" onClick={() => navigate(-1)} className="w-full rounded-md mb-2.5 p-2.5 bg-[#f8ad3c] text-white cursor-pointer opacity-80 hover:opacity-100">
						Kembali
					</button>
				</div>
			</div>
		</div>)
	}
}

